'use client';
import React, { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import ProfileHero from './components/ProfileHero';
import InterpretationPanel from './components/InterpretationPanel';
import RawDataTable from './components/RawDataTable';
import ProfileRadar from './components/ProfileRadar';
import { generateInterpretation } from '@/lib/scoring';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Activity, ClipboardList } from 'lucide-react';

function ExplosiveProfileReportContent() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const queryAthleteId = searchParams.get('athleteId');

  const [session, setSession] = useState<any>(null);
  const [athlete, setAthlete] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    async function load() {
      setLoading(true);

      let profileQuery = supabase
        .from('athlete_profiles')
        .select('id, name, age, sport, position, height_in, weight_lbs, training_age, injury_history, season_phase, avatar_initials');

      if (queryAthleteId) {
        profileQuery = profileQuery.eq('id', queryAthleteId);
      } else {
        profileQuery = profileQuery.eq('user_id', user!.id);
      }

      const { data: profile } = await profileQuery.single();

      if (profile) {
        setAthlete({
          id: profile.id,
          name: profile.name ?? '',
          age: profile.age ?? 0,
          sport: profile.sport ?? '',
          position: profile.position ?? '',
          heightIn: Number(profile.height_in ?? 0),
          weightLbs: Number(profile.weight_lbs ?? 0),
          trainingAge: Number(profile.training_age ?? 0),
          injuryHistory: profile.injury_history ?? '',
          seasonPhase: profile.season_phase ?? '',
          avatarInitials: profile.avatar_initials ?? (profile.name ?? '??').slice(0, 2).toUpperCase(),
        });

        const { data: sessionData } = await supabase
          .from('test_sessions')
          .select('*')
          .eq('athlete_id', profile.id)
          .order('session_date', { ascending: false })
          .limit(1)
          .single();

        if (sessionData) {
          setSession({
            id: sessionData.id,
            athleteId: sessionData.athlete_id,
            date: sessionData.session_date,
            engineScore: Number(sessionData.engine_score),
            ignitionScore: Number(sessionData.ignition_score),
            bounceScore: Number(sessionData.bounce_score),
            eps: Number(sessionData.eps),
            trapBarDL: Number(sessionData.trap_bar_dl ?? 0),
            backSquat: Number(sessionData.back_squat ?? 0),
            frontSquat: Number(sessionData.front_squat ?? 0),
            splitSquat: Number(sessionData.split_squat ?? 0),
            cmjHeight: Number(sessionData.cmj_height ?? 0),
            sqjHeight: Number(sessionData.sqj_height ?? 0),
            sprint10yd: Number(sessionData.sprint_10yd ?? 0),
            sprint20yd: Number(sessionData.sprint_20yd ?? 0),
            medBallThrow: Number(sessionData.med_ball_throw ?? 0),
            rsi: Number(sessionData.rsi ?? 0),
            dropJumpHeight: Number(sessionData.drop_jump_height ?? 0),
            broadJump: Number(sessionData.broad_jump ?? 0),
            tripleJump: Number(sessionData.triple_jump ?? 0),
            pogoHeight: Number(sessionData.pogo_height ?? 0),
          });
        }
      }
      setLoading(false);
    }

    load();
  }, [user, queryAthleteId]);

  const profile = session
    ? generateInterpretation(session.engineScore, session.ignitionScore, session.bounceScore)
    : null;

  if (loading) {
    return (
      <AppLayout activePath="/explosive-profile-report">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-zinc-500">Loading profile report...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activePath="/explosive-profile-report">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">Explosive Profile Report</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {athlete?.name && `${athlete.name} · `}
              {session
                ? new Date(session.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : 'No assessment data'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {queryAthleteId && (
              <a href="/coach-dashboard" className="btn-ghost text-sm">
                ← Back to Dashboard
              </a>
            )}
            <a
              href={queryAthleteId ? `/training-program-builder?athleteId=${queryAthleteId}` : '/training-program-builder'}
              className="btn-primary"
            >
              Build Training Program →
            </a>
          </div>
        </div>

        {!session ? (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}
            >
              <Activity size={28} style={{ color: '#E10600' }} />
            </div>
            <h3 className="text-lg font-semibold text-zinc-200 mb-2">No Profile Data Yet</h3>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto">
              Complete an explosive performance assessment to generate your full profile report with Engine, Ignition, and Bounce scores.
            </p>
            <a href="/assessment-entry" className="btn-primary inline-flex items-center gap-2">
              <ClipboardList size={15} />
              Start Assessment
            </a>
          </div>
        ) : (
          <>
            <ProfileHero profile={profile!} athlete={athlete} session={session} />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <InterpretationPanel profile={profile!} />
              </div>
              <div className="lg:col-span-2">
                <ProfileRadar engine={profile?.engineScore ?? 0} ignition={profile?.ignitionScore ?? 0} bounce={profile?.bounceScore ?? 0} />
              </div>
            </div>
            <RawDataTable session={session} athlete={athlete} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

export default function ExplosiveProfileReportPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    }>
      <ExplosiveProfileReportContent />
    </Suspense>
  );
}